import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, MapPin, ArrowRight } from "lucide-react";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { Reveal, Spinner } from "@/components/public/Primitives";
import { SOCIAL_WORK_CATEGORIES } from "@/data/constants";
import { useLanguage } from "@/context/LanguageContext";
import { getSocialWork } from "@/lib/api";

export default function SocialWork() {
  const [category, setCategory] = useState("All");
  const { tr } = useLanguage();
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["social-work", category],
    queryFn: () => getSocialWork(category === "All" ? undefined : category),
  });

  return (
    <div data-testid="social-work-page">
      <PageSeo
        slug="social-work"
        title="Social Work | Girish Kumar Naidu"
        description="Explore the social work of Girish Kumar Naidu — education, healthcare, food relief, women empowerment, youth development and more across Bengaluru."
        keywords="Girish Kumar Naidu social work, social service Bengaluru, community programs"
        schemas={[breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Social Work" }])]}
      />
      <PageHeader
        overline={tr("page.social.overline")}
        title={tr("page.social.title")}
        subtitle={tr("page.social.subtitle")}
        crumbs={[{ name: "Social Work" }]}
      />

      <section className="bg-white py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex flex-wrap gap-2 mb-12" data-testid="social-work-filters">
            {SOCIAL_WORK_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                  category === c ? "bg-gold text-white" : "bg-paper border border-gray-200 text-gray-600 hover:border-gold hover:text-gold"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {isLoading ? (
            <Spinner />
          ) : posts.length === 0 ? (
            <p className="text-center text-gray-500 py-20">No posts in this category yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.08}>
                  <Link to={`/social-work/${p.slug}`} className="group block bg-white rounded-2xl overflow-hidden border border-gray-200 card-lift h-full">
                    <div className="h-52 overflow-hidden relative">
                      <img src={p.featuredImage} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <span className="absolute top-4 left-4 px-3 py-1 rounded-full font-mono text-[10px] font-bold uppercase tracking-wider bg-gold text-white">{p.category}</span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 font-mono text-[11px] text-gray-500 mb-3 flex-wrap">
                        <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {p.date}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.location}</span>
                      </div>
                      <h3 className="font-display text-lg font-bold text-navy mb-2 group-hover:text-gold transition-colors line-clamp-2">{p.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 mb-4">{p.shortDescription}</p>
                      <span className="inline-flex items-center gap-1.5 text-sm font-bold text-navy group-hover:text-gold transition-colors">
                        Read more <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
